import React, { forwardRef, useImperativeHandle, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@material-ui/core/Dialog'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useSelector } from 'react-redux'
import numeral from 'numeral'
import moment from 'moment'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import styles from './styles.module.scss'

import { CrossIcon } from '../icons'
import Table from '../../../Invoices/Table'

import { getCurrency } from '../../../../helpers/currencies'

const columnHelper = createColumnHelper()

const formatNumber = (value, currency) => {
  return `${numeral(value).format('0,0.[00]')}${currency}`
}

const getProfit = (data) => {
  const sales = data.salesAmount || 0
  const rawMaterialCost = data.transactions.reduce((sum, transaction) => sum + (transaction['ProductCostBase.ProductCost'] || 0), 0)
  const employeeCost = data.employeeCost || 0
  const profit = sales - rawMaterialCost - employeeCost
  return sales > 0 ? (profit / sales * 100) : 0
}

const EmployeeSalesModal = forwardRef(({ employee }, ref) => {
  const { t } = useTranslation()

  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)
  
  const [open, setOpen] = useState(false)
  const [data, setData] = useState({transactions: [], name: ''})

  useImperativeHandle(ref, () => ({
    open: (data) => {
      setOpen(true)
      setData(data)
    }
  }))

  const transactions = useMemo(() => {
    const salesByDish = data.transactions.reduce((acc, transaction) => {
      const name = transaction.DishName || 'Unknown'
      return {
        ...acc,
        [name]: acc[name] ? [...acc[name], transaction] : [transaction]
      }
    }, {})
    return Object.entries(salesByDish).map(([dishName, transactions]) => {
      return {
        dish: dishName,
        category: transactions[0].DishCategory || '-',
        quantity: transactions.reduce((sum, transaction) => sum + transaction.DishAmountInt, 0),
        amount: transactions.reduce((sum, transaction) => sum + transaction.DishDiscountSumInt, 0),
      }
    }).sort((a, b) => b.amount - a.amount).slice(0, 10)
  }, [data.transactions])

  const chartData = useMemo(() => {
    const salesByDate = data.transactions.reduce((acc, transaction) => {
      const date = transaction['OpenDate.Typed']
      return {
        ...acc,
        [date]: acc[date] ? [...acc[date], transaction] : [transaction]
      }
    }, {})
    return Object.entries(salesByDate).map(([date, transactions]) => {
      return {
        date,
        sales: transactions.reduce((sum, transaction) => sum + transaction.DishDiscountSumInt, 0),
      }
    })
  }, [data.transactions])

  console.log('chartData', chartData)

  const columns = useMemo(() => ([
    columnHelper.accessor('dish', {
      id: 'dish',
      header: t('Dish'),
      enableSorting: false,
      cell: ({ getValue }) => {
        return getValue()
      }
    }),
    columnHelper.accessor('category', {
      id: 'category',
      header: t('Dish category'),
      enableSorting: false,
      cell: ({ getValue }) => {
        return getValue()
      }
    }),
    columnHelper.accessor('quantity', {
      id: 'quantity',
      enableSorting: false,
      cell: ({ getValue }) => getValue(),
      header: t('Quantity'),
    }),
    columnHelper.accessor('amount', {
      id: 'amount',
      enableSorting: false,
      cell: ({ getValue }) => formatNumber(getValue(), currency.symbol),
      header: t('Amount'),
    }),
  ]), [t, currency.symbol])

  const handleClose = () => {
    setOpen(false)
  }

  const table = useReactTable({
    data: transactions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Dialog onClose={() => handleClose(false)} maxWidth={'lg'} open={open}>
      <div className={styles.container}>
        <div className={styles.header}>
          { data.name }
          <div className={styles.closeButton} onClick={handleClose}>
            <CrossIcon />
          </div>
        </div>
        <div className={styles.cards}>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Sales') }</div>
            <div className={styles.cardValue}>{ formatNumber(data.salesAmount, currency.symbol) }</div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Bills') }</div>
            <div className={styles.cardValue}>{ data.bills }</div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Bills') }</div>
            <div className={styles.cardValue}>{ data.bills }</div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Avg. check') }</div>
            <div className={styles.cardValue}>{ formatNumber(data.avgBill, currency.symbol) }</div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Profit %') }</div>
            <div className={styles.cardValue}>{ numeral(getProfit(data)).format('0.[00]') }%</div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Target') }</div>
            <div className={styles.cardValue}>{ formatNumber(data.salesTarget, currency.symbol) }%</div>
          </div>
          <div className={styles.card}>
            <div className={styles.cardTitle}>{ t('Bonus') }</div>
            <div className={styles.cardValue}>{ formatNumber(data.bonusAmount, currency.symbol) }</div>
          </div>
        </div>
        <div>
          <div className={styles.sectionTitle}>{ t('Top 10 dishes (by sales)') }</div>
          <Table
            table={table}
            columns={columns}
            wrapperStyle={{flex: 1}}
            headerClassName={styles.tableHeader} />
        </div>
        <div style={{height: 400}}>
          <div className={styles.sectionTitle}>{ t('Sales by day') }</div>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="oklch(0.6 0.17 155)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="oklch(0.6 0.17 155)" stopOpacity={0.4} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="oklch(0.92 0.008 240)" />
              <XAxis dataKey="date" stroke="oklch(0.5 0.02 250)" fontSize={12} tickFormatter={(v) => moment(v).format('ddd, DD')} />
              <YAxis yAxisId="left" stroke="oklch(0.5 0.02 250)" />
              <Tooltip
                contentStyle={{
                  background: "oklch(1 0 0)",
                  border: "1px solid oklch(0.92 0.008 240)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(value, name) => {
                  return [formatNumber(value, currency.symbol), name];
                }}
                labelFormatter={(l) => l}
              />
              <Bar yAxisId="left" dataKey="sales" name={t('Sales')} fill="url(#salesGrad)" radius={[6, 6, 0, 0]} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Dialog>
  )
})

export default EmployeeSalesModal
